import Table from "@mui/material/Table"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableCell from "@mui/material/TableCell"
import React from "react"
import TableBody from "@mui/material/TableBody"
import TableContainer from "@mui/material/TableContainer"
import { SxProps, Theme } from "@mui/material"

export interface MuiTableColumn<T> {
    id: keyof T | "index" | "custom"
    label: string | React.ReactNode | ""
    width?: number
    align?: "right" | "center" | "left"
    format?: (value: T[keyof T], row: T, rowIndex: number) => string
    render?: (value: T[keyof T], row: T, rowIndex: number) => React.ReactNode
}

type Props<T> = {
    columns: MuiTableColumn<T>[]
    rows: T[]
    containerSx?: SxProps<Theme>
    stickyHeader?: boolean
}

const tableHeaderCellStyles: SxProps<Theme> = {
    color: "#70758D",
    fontSize: "11px",
    padding: "8px",
    borderBottom: "none",
}

const tableCellStyles: SxProps<Theme> = {
    color: "#101941",
    fontSize: "12px",
    padding: "8px",
}

const MuiTable = <T extends object>(props: Props<T>) => {
    const { columns, rows, containerSx = {} } = props

    const renderCell = (row: T, column: MuiTableColumn<T>, rowIndex: number) => {
        if (Object.keys(column).length === 0) return
        if (column.id === "index") return rowIndex + 1
        if (column.id === "custom") {
            if (column.render) return column.render(undefined as any, row, rowIndex)
            return "-"
        }
        if (!row[column.id] && !column.render) return "-"

        const value = row[column.id]

        if (column.render) return column.render(value, row, rowIndex)
        if (column.format) return column.format(value, row, rowIndex)

        return String(value)
    }

    return (
        <TableContainer sx={containerSx}>
            <Table stickyHeader={props.stickyHeader}>
                <TableHead>
                    <TableRow>
                        {columns.map((column, index) => (
                            <TableCell     
                                size="small"
                                key={column.id ? String(column.id) : index}
                                align={column.align}
                                sx={{ ...tableHeaderCellStyles, width: column.width }}>
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={`row-${rowIndex}`}>
                            {columns.map((column, colIndex) => (
                                <TableCell
                                    key={`cell-${rowIndex}-${String(column.id)}`}
                                    align={column.align}
                                    sx={{
                                        ...tableCellStyles,
                                        borderBottom: rowIndex === rows.length - 1 ? "none" : undefined,
                                        width: column.width,
                                    }}>
                                    {renderCell(row, column, rowIndex)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default MuiTable
